import React, { useEffect, useState } from 'react';
import { Checkbox, InputNumber } from 'antd';
import { useResolutions } from '../../resolution/hooks/use-resolutions';
import { usePieceDispatch } from '../../model/piece-context';
import { useSelectedLayer, useSelectedLayerId } from '../../selection/hooks/use-selected-layer';
import { TextContent } from '../../model/types';
import type { AutoFitConfig } from '../../text/utils/auto-fit-textbox';
import { AutoFitTextbox } from '../types';
import {
  getAutoFitTextMaxFontSize,
  getAutoFitTextMaxHeight,
  getAutoFitTextMinFontSize,
  getAutoFitTextSnapTo,
  getAutoFitTextSplitByGrapheme,
  getAutoFitTextWidth,
  setAutoFitTextMaxFontSize,
  setAutoFitTextMaxHeight,
  setAutoFitTextMinFontSize,
  setAutoFitTextSnapTo,
  setAutoFitTextSplitByGrapheme,
  setAutoFitTextWidth
} from '../utils/auto-fit-text-accessors';
import { labelStyle, rowStyle, sectionStyle, sliderStyle, valueLabelStyle } from './auto-fit-text-styles';

type Props = { textbox: AutoFitTextbox };

const FALLBACK_MAX = 2000;

export const AutoFitTextConstraintsRow: React.FC<Props> = ({ textbox }) => {
  const { activeResolution } = useResolutions();
  const dispatch = usePieceDispatch();
  const layerId = useSelectedLayerId();
  const layer = useSelectedLayer();

  const maxCanvasWidth = activeResolution?.canvasWidth ?? FALLBACK_MAX;
  const maxCanvasHeight = activeResolution?.canvasHeight ?? FALLBACK_MAX;

  const [width, setWidth] = useState(getAutoFitTextWidth(textbox));
  const [maxHeight, setMaxHeight] = useState(getAutoFitTextMaxHeight(textbox));
  const [minFontSize, setMinFontSize] = useState(getAutoFitTextMinFontSize(textbox));
  const [maxFontSize, setMaxFontSize] = useState(getAutoFitTextMaxFontSize(textbox));
  const [snapTo, setSnapTo] = useState(getAutoFitTextSnapTo(textbox));
  const [splitByGrapheme, setSplitByGrapheme] = useState(getAutoFitTextSplitByGrapheme(textbox));

  // Sync the controls from the model.
  useEffect(() => {
    if (layer?.kind !== 'text') return;
    const c = layer.content;
    setWidth(c.width ?? 0); // auto-fit text always keeps its width; the fallback satisfies the type
    setSplitByGrapheme(c.splitByGrapheme ?? false);
    if (c.autoFit) {
      setMaxHeight(c.autoFit.maxHeight);
      setMinFontSize(c.autoFit.minFontSize);
      setMaxFontSize(c.autoFit.maxFontSize);
      setSnapTo(c.autoFit.snapTo ?? 1);
    }
  }, [layer]);

  const patch = (p: Partial<TextContent>) => {
    if (layerId) dispatch({ type: 'patchLayerContent', id: layerId, patch: p });
  };

  // autoFit is a nested object; merge the changed field onto the model's current config.
  const patchAutoFit = (p: Partial<AutoFitConfig>) => {
    if (layer?.kind !== 'text' || !layer.content.autoFit) return;
    patch({ autoFit: { ...layer.content.autoFit, ...p } });
  };

  // Sliders: the accessor mutation is the live preview; the commit records to the model.
  const onWidth = (v: number) => {
    setWidth(v);
    setAutoFitTextWidth(textbox, v);
  };
  const commitWidth = () => patch({ width });

  const onMaxHeight = (v: number) => {
    setMaxHeight(v);
    setAutoFitTextMaxHeight(textbox, v);
  };
  const commitMaxHeight = () => patchAutoFit({ maxHeight });

  const onMinFontSize = (v: number | null) => {
    if (v == null) return;
    setMinFontSize(v);
    setAutoFitTextMinFontSize(textbox, v);
    patchAutoFit({ minFontSize: v });
  };

  const onMaxFontSize = (v: number | null) => {
    if (v == null) return;
    setMaxFontSize(v);
    setAutoFitTextMaxFontSize(textbox, v);
    patchAutoFit({ maxFontSize: v });
  };

  const onSnapTo = (v: number | null) => {
    if (v == null) return;
    setSnapTo(v);
    setAutoFitTextSnapTo(textbox, v);
    patchAutoFit({ snapTo: v });
  };

  const onSplitByGrapheme = (checked: boolean) => {
    setSplitByGrapheme(checked);
    setAutoFitTextSplitByGrapheme(textbox, checked);
    patch({ splitByGrapheme: checked });
  };

  return (
    <div style={sectionStyle}>
      <div style={labelStyle}>Constraints</div>

      <div style={rowStyle}>
        <span style={{ ...labelStyle, minWidth: 80, marginBottom: 0 }}>Ancho</span>
        <input
          type='range'
          min={50}
          max={maxCanvasWidth}
          step={1}
          value={width}
          onChange={e => onWidth(Number(e.target.value))}
          onPointerUp={commitWidth}
          style={sliderStyle}
        />
        <span style={valueLabelStyle}>{Math.round(width)}px</span>
      </div>

      <div style={{ ...rowStyle, marginTop: 6 }}>
        <span style={{ ...labelStyle, minWidth: 80, marginBottom: 0 }}>Alto máx.</span>
        <input
          type='range'
          min={20}
          max={maxCanvasHeight}
          step={1}
          value={maxHeight}
          onChange={e => onMaxHeight(Number(e.target.value))}
          onPointerUp={commitMaxHeight}
          style={sliderStyle}
        />
        <span style={valueLabelStyle}>{Math.round(maxHeight)}px</span>
      </div>

      <div style={{ ...rowStyle, marginTop: 8 }}>
        <span style={{ ...labelStyle, minWidth: 80, marginBottom: 0 }}>Font</span>
        <InputNumber
          value={minFontSize}
          onChange={onMinFontSize}
          min={1}
          max={400}
          size='small'
          style={{ width: 70 }}
        />
        <span style={{ ...labelStyle, marginBottom: 0 }}>—</span>
        <InputNumber
          value={maxFontSize}
          onChange={onMaxFontSize}
          min={1}
          max={400}
          size='small'
          style={{ width: 70 }}
        />
      </div>

      <div style={{ ...rowStyle, marginTop: 8 }}>
        <span style={{ ...labelStyle, minWidth: 80, marginBottom: 0 }}>Snap</span>
        <InputNumber value={snapTo} onChange={onSnapTo} min={1} max={20} size='small' style={{ width: 70 }} />
      </div>

      <div style={{ ...rowStyle, marginTop: 10 }}>
        <Checkbox
          checked={splitByGrapheme}
          onChange={e => onSplitByGrapheme(e.target.checked)}
          style={{ fontSize: 12 }}
        >
          Cortar palabras al ajustar ancho
        </Checkbox>
      </div>
    </div>
  );
};
